import { useAppContext } from '@/context';
import { logout } from '@/utils/auth';
import { getJson } from '@/utils/fetchOptions';

import { useAuthToken, useQuery } from '..';

interface UseCollaborationsProps {
  currentPage?: number;
  perPage?: number;
  type?: string;
  text?: string;
}

export function useCollaborationsInvite() {
  const { setAlert } = useAppContext();
  const token = useAuthToken();
  const {
    isLoading,
    data = {},
    refetch,
    error
  } = useQuery({
    queryKey: `influencer-collaborations-invites`,
    queryFn: async () =>
      getJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/influencer/invites`,
        token
      }).then(
        (
          response: {
            invites: Api.Influencer.CollaborationSearchItem[];
          } & ResponseObject
        ) => {
          if ((response as ResponseExpiredToken).isExpired) {
            logout();
          }

          if ('code' in response) {
            setAlert(response.code);
          }
          return response;
        }
      ),
    enabled: !!token
  });

  return {
    invites: data || {},
    isLoading,
    refetch,
    error
  };
}

export function useCollaborations({ currentPage, type, perPage, text }: UseCollaborationsProps) {
  const { setAlert } = useAppContext();
  const token = useAuthToken();
  const {
    isLoading,
    data = {},
    refetch,
    error
  } = useQuery({
    queryKey: `influencer-collaborations-${currentPage}-${type}-${perPage}-${text}`,
    queryFn: async () =>
      getJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/influencer/collaborations/search?status=${type}&text=${text}&page_size=${perPage}&page_number=${currentPage}`,
        token
      }).then(
        (
          responseAccepted: {
            page_number: number;
            page_size: number;
            count_total: number;
            has_more: true;
            collaborations: Api.Influencer.CollaborationSearchItem[];
          } & ResponseObject
        ) => {
          if ((responseAccepted as ResponseExpiredToken).isExpired) {
            logout();
          }

          if ('code' in responseAccepted) {
            setAlert(responseAccepted.code);
          }

          return {
            pageNumber: responseAccepted.page_number,
            pageSize: responseAccepted.page_size,
            countTotal: responseAccepted.count_total,
            hasMore: responseAccepted.has_more,
            collaborations: responseAccepted.collaborations
          };
        }
      ),
    enabled: !!token
  });

  return {
    data: data || {},
    isLoading,
    refetch,
    error
  };
}
